import React, { useEffect, useState } from 'react';
import PriceCalculatorForm from '../../components/priceCalculatorForm/PriceCalculatorForm';
import CustomerBackground from '../../components/customerBackground/CustomerBackground';
import { Accordion, AccordionContent, AccordionPanel, AccordionTitle, Footer, FooterCopyright, FooterLink, FooterLinkGroup } from "flowbite-react";
import { LocalizeSelector } from '../../components/localize/LocalizeSelector';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import strings, { selectLanguage } from '../../components/localize/localizeSlice';
import CookieConsent from '../../components/cookieConsent/CookieConsent';
import LoginInfo from '../../components/loginInfo/LoginInfo';
import { logout } from "../../components/priceCalculatorForm/PriceCalculatorFormSlice";


declare const grecaptcha: any;

const COOKIE_CONSENT_KEY = 'cookieConsent';
const RECAPTCHA_SITE_KEY = "6LfNjVcrAAAAAPN8AR7fQRXj34vvVFLLTkW3U5-j";


const PriceCalculatorPage: React.FC = () => {
    useAppSelector(selectLanguage);
    const [isCookieConsentVisible, setCookieConsentVisible] = useState(true);
    const [recaptchaScriptLoaded, setRecaptchaScriptLoaded] = useState(false);
    const dispatch = useAppDispatch();
    const showDemoOverlay =
        typeof window !== "undefined" &&
        (
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1" ||
            window.location.hostname === "public.et360.de"
        );

    const loadRecaptchaScript = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (recaptchaScriptLoaded) {
                return resolve();
            }

            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + RECAPTCHA_SITE_KEY;
            script.async = true;
            script.onload = () => {
                setRecaptchaScriptLoaded(true);
                grecaptcha.ready(resolve);
            };
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    const loadRecaptchaKey = async (): Promise<string | null> => {
        try {
            await loadRecaptchaScript();
            const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
            return token;
        } catch (err) {
            console.error('reCAPTCHA or form submission failed', err);
            alert("Something went wrong. Please try again.");
            return null;
        }
    }

    useEffect(() => {
        const consentState = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consentState) {
            setCookieConsentVisible(true);
        }
        else {
            setCookieConsentVisible(false);
        }
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e: { preventDefault: () => void; returnValue: string; }) => {
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const handleCookieConsentAccept = () => {
        localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
        setCookieConsentVisible(false);
    };

    return (
        <>
            {/* DEMONSTRATION Overlay */}
            {showDemoOverlay && (<div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    pointerEvents: "none",
                    zIndex: 9999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <span
                    style={{
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "7vw",
                        opacity: 0.25,
                        textShadow: "2px 2px 8px #000",
                        userSelect: "none",
                        textAlign: "center"
                    }}
                >
                    DEMO – keine echte Bestellung
                </span>
            </div>)}
            <CustomerBackground />
            <div className='absolute top-5 right-5 p-4 w-40 mt-4 ml-auto sm:hidden'>
                <LocalizeSelector></LocalizeSelector>
            </div>
            <div className="relative flex flex-col justify-center items-center min-h-screen mt-14 ">
                {/* Form */}
                <div
                    className="w-full max-w-4xl shadow-2xl shadow-gray-950 bg-white dark:bg-gray-800 relative z-10 md:mt-85 mt-0"
                    style={{ top: '5rem', position: 'absolute' }}
                >
                    <h1
                        className="!text-4xl block md:hidden"
                        style={{ fontFamily: 'Anton, sans-serif' }}
                    >
                        <span className="relative inline-block font-extrabold m-5 mb-2 ">
                            <span className="relative z-10 text-gray-800 dark:text-white">{strings.priceCalculator}</span>
                            <span
                                className="absolute left-0 bottom-[-5px] bg-cuno-500 h-5 w-full -skew-x-12 z-[-1]"
                            ></span>
                        </span>
                    </h1>
                    <div className="justify-between items-center w-full px-4 py-8 hidden sm:flex">
                        <div>
                            <LoginInfo onLogout={() => { dispatch(logout()) }} />
                        </div>
                        <div>
                            <LocalizeSelector />
                        </div>
                    </div>

                    <div className='flex justify-start p-4 w-full mb-4  mr-auto sm:hidden'>
                        <LoginInfo onLogout={() => { dispatch(logout()) }} />
                    </div>
                    {isCookieConsentVisible &&
                        <CookieConsent onAccept={handleCookieConsentAccept} onDecline={() => { }} privacyPolicyLink="https://cuno.gmbh/datenschutzerklaerung/" imprintLink="https://cuno.gmbh/impressum/" />
                    }
                    <PriceCalculatorForm  onLoadRecaptchaToken={loadRecaptchaKey} />
                    <div className='!bg-gray-50 dark:!bg-transparent'>
                        {/* FAQ Accordion */}
                        <Accordion className='rounded-none p-2 mt-10' >
                            <AccordionPanel >
                                <AccordionTitle className='hidden collapse'></AccordionTitle>
                                <AccordionContent className='hidden collapse'>
                                </AccordionContent>
                            </AccordionPanel>
                            <AccordionPanel>
                                <AccordionTitle>
                                    {strings.faq.isMandatoryOrder.question}
                                </AccordionTitle>
                                <AccordionContent>
                                    <p className="mb-2 text-gray-500 dark:text-gray-400">
                                        {strings.faq.isMandatoryOrder.answer}
                                    </p>
                                </AccordionContent>
                            </AccordionPanel>
                            <AccordionPanel>
                                <AccordionTitle>{strings.faq.moreHelpNeeded.question}</AccordionTitle>
                                <AccordionContent>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {strings.faq.moreHelpNeeded.answer}
                                    </p>
                                </AccordionContent>
                            </AccordionPanel>
                        </Accordion>

                        <Footer container className='bg-gray-50'>
                            <FooterCopyright href="#" by="et360 GmbH" year={2025} />
                            <FooterLinkGroup>
                                <FooterLink href="https://cuno.gmbh/impressum/">Impressum Cuno GmbH</FooterLink>
                                <FooterLink href="https://cuno.gmbh/datenschutzerklaerung/">Privacy Policy Cuno GmbH</FooterLink>
                                <FooterLink href="https://www.et360.de/privacypolicy.php">Privacy Policy et360</FooterLink>
                                <FooterLink href="https://www.et360.de/privacypolicy.php">Imprint et360</FooterLink>
                            </FooterLinkGroup>
                        </Footer>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PriceCalculatorPage;